'use client';
import React, { useEffect, useState } from 'react';
import { useParams } from 'next/navigation';

import '../../../app/hubspot-form.css';

interface HubspotFormProps {
  portalId?: string;
  formId?: string;
  formTarget?: string;
  redirectUrl?: string;
  className?: string;
  region?: string;
  onFormReady?: () => void;
  onFormSubmitted?: () => void;
}

const HubspotForm: React.FC<HubspotFormProps> = ({
  portalId,
  formId,
  redirectUrl,
  formTarget = 'form',
  className = '',
  region,
  onFormReady,
  onFormSubmitted,
}) => {
  const { locale } = useParams<{ locale: 'en' | 'fr' | 'es' }>();
  const localeRedirectURL = locale === 'en' ? redirectUrl : `/${locale}${redirectUrl}`;
  const [formVisible, setFormVisible] = useState(false);

  useEffect(() => {
    const loadHubspot = () => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      window.hbspt?.forms.create({
        portalId,
        formId,
        target: `#${formTarget}`,
        redirectUrl: localeRedirectURL,
        region,
        onFormReady: () => {
          setTimeout(() => {
            const formContainer = document.getElementById(formTarget);
            if (!formContainer) return;

            const translations: Record<'en' | 'fr' | 'es', Record<string, string>> = {
              en: {},
              fr: {
                'First name': 'Prénom',
                'First Name*': 'Prénom',
                'Enter your first name*': 'Prénom',
                'Last name': 'Nom',
                'Last Name*': 'Nom',
                'Enter your last name*': 'Nom',
                'Email address': 'Adresse e-mail',
                'Email Address*': 'Adresse e-mail',
                'Enter your email address*': 'Adresse e-mail',
                Email: 'E-mail',
                'Phone number': 'Numéro de téléphone',
                'Enter your phone number': 'Numéro de téléphone',
                Region: 'Région',
                Message: 'Message',
                'Add your message': 'Message',
                Organization: 'Organisation',
                'Organisation*': 'Organisation',
                Country: 'Pays',
                'Country*': 'Pays',
                Mobile: 'Téléphone',
                'Interested in': 'Intéressé par',
                'All Products': 'Tous les produits',
                Other: 'Autres',
                'Your message': 'Votre message',
                'Enter your first name': 'Saisissez votre prénom',
                'Enter your last name': 'Saisissez votre nom de famille',
                'Enter your email address': 'Saisissez votre adresse électronique',
                'Enter your organization': 'Entrez votre organisation',
                'Enter your mobile number': 'Saisissez votre numéro de téléphone portable',
                'Select your country': 'Veuillez sélectionner',
                'Please complete this required field.': 'Veuillez remplir ce champ obligatoire.',
                Submit: 'Envoyer',
              },
              es: {
                'First name': 'Nombre',
                'First Name*': 'Nombre',
                'Enter your first name*': 'Nombre',
                'Last name': 'Apellido',
                'Last Name*': 'Apellido',
                'Enter your last name*': 'Apellido',
                'Email addres': 'Correo Electrónico',
                'Email Address*': 'Correo Electrónico',
                'Enter your email address*': 'Correo Electrónico',
                Email: 'Correo Electrónico',
                'Phone number': 'Número de teléfono',
                'Enter your phone number': 'Número de teléfono',
                Region: 'Región',
                Message: 'Mensaje',
                'Add your message': 'Mensaje',
                Organization: 'Organización',
                'Organisation*': 'Organización',
                Country: 'País',
                'Country*': 'País',
                Mobile: 'Teléfono',
                'Interested in': 'Interesado en',
                'All Products': 'Todos los productos',
                Other: 'Otros',
                'Your message': 'Su mensaje',
                'Enter your first name': 'Introduzca su nombre',
                'Enter your last name': 'Introduzca su apellido',
                'Enter your email address': 'Introduzca su dirección de correo electrónico',
                'Enter your organization': 'Introduzca su organización',
                'Enter your mobile number': 'Introduzca su número de móvil',
                'Select your country': 'Por favor seleccione',
                'Please complete this required field.': 'Rellene este campo obligatorio.',
                Submit: 'Enviar',
              },
            };

            const currentTranslations = translations[locale] || {};

            formContainer.querySelectorAll('label').forEach((label) => {
              const span = label.querySelector('span');
              const textNode = span || label;
              const key = textNode.textContent?.trim();
              if (key && currentTranslations[key]) {
                textNode.textContent = currentTranslations[key];
              }
            });

            formContainer.querySelectorAll('.hs-error-msg').forEach((err) => {
              const txt = err.textContent?.trim();
              if (txt && currentTranslations[txt]) {
                err.textContent = currentTranslations[txt];
              }
            });

            formContainer.querySelectorAll('input').forEach((input) => {
              const ph = input.getAttribute('placeholder')?.trim();
              if (ph && currentTranslations[ph]) {
                input.setAttribute('placeholder', currentTranslations[ph]);
              }
            });

            formContainer.querySelectorAll('select option').forEach((opt) => {
              const txt = opt.textContent?.trim();
              if (txt && currentTranslations[txt]) {
                opt.textContent = currentTranslations[txt];
              }
            });

            formContainer.querySelectorAll("input[type='submit']").forEach((btn) => {
              const val = btn.getAttribute('value')?.trim();
              if (val && currentTranslations[val]) {
                btn.setAttribute('value', currentTranslations[val]);
              }
            });

            formContainer.querySelectorAll('textarea').forEach((textarea) => {
              const ph = textarea.getAttribute('placeholder')?.trim();
              if (ph && currentTranslations[ph]) {
                textarea.setAttribute('placeholder', currentTranslations[ph]);
              }
            });

            // Reveal form
            setFormVisible(true);
            onFormReady?.();
          }, 0);
        },
        onFormSubmitted,
      });
    };

    if ((window as any).hbspt) {
      loadHubspot();
    } else {
      const script = document.createElement('script');
      script.src = 'https://js.hsforms.net/forms/v2.js';
      script.id = 'hs-script-loader';
      script.onload = loadHubspot;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [portalId, formId, redirectUrl, formTarget, className, region, locale, onFormReady, onFormSubmitted]);

  return (
    <div
      id={formTarget}
      className={`${className} valdHsFormBlock`}
      style={{
        minHeight: '560px', // Approximate height of the form
        visibility: formVisible ? 'visible' : 'hidden',
        opacity: formVisible ? 1 : 0,
      }}
    />
  );
};

export default HubspotForm;
